import React from 'react';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';

// ============================================================================
// Privacy Policy — verbatim copy from the KBT-LA "Privacy Policy" doc
// (version date 1/1/2023, originally drafted for kbtla.org). Lightly
// re-styled for the editorial shell; the legal content is unchanged.
// ============================================================================

const SECTIONS = [
  {
    head: 'General',
    body: [
      'Kehilat Bnei Torah ("KBT," "we," "us," or "our") respects the privacy of its members, donors, and visitors ("you" or "user"). This Privacy Policy outlines how we collect, use, and safeguard your information when you visit our website at https://www.kbtla.org (the "Website"). By using our Website, you consent to the terms of this Privacy Policy.',
    ],
  },
  {
    head: 'Information We Collect',
    body: [
      'We may collect the following types of information when you visit or interact with our Website:',
    ],
    list: [
      'Personal Information — includes your name, email address, phone number, billing information, and any other information you voluntarily provide through donation forms, contact forms, or event registration.',
      'Non-Personal Information — includes your IP address, browser type, device information, and general usage data collected automatically through cookies or analytics tools.',
    ],
  },
  {
    head: 'How We Use Your Information',
    body: ['Your information is used to:'],
    list: [
      'Process donations, send receipts, and provide tax-related documentation.',
      'Communicate updates about KBT events, programs, and fundraising campaigns.',
      'Improve our Website and user experience.',
      'Respond to your inquiries or requests.',
      'Comply with applicable laws and regulations.',
    ],
  },
  {
    head: 'Cookies and Analytics',
    body: [
      'We use cookies and similar technologies to enhance your browsing experience and collect usage data. Cookies help us understand how you interact with our Website and allow us to improve its functionality.',
      'How to Manage Cookies: You can disable cookies in your browser settings. However, this may affect some features of the Website.',
    ],
  },
  {
    head: 'Third-Party Services',
    body: [
      'We may use trusted third-party platforms for payment processing (e.g., donation platforms like CauseMatch) and email communications. These platforms have their own privacy policies, and we encourage you to review them.',
    ],
  },
  {
    head: 'Sharing Your Information',
    body: [
      'KBT will never sell or rent your personal information. We may share information in the following cases:',
    ],
    list: [
      'To comply with legal obligations or protect our rights.',
      'With third-party service providers assisting us in processing donations, managing our database, or hosting our Website.',
      'In aggregate form (without identifying you) for reporting or promotional purposes.',
    ],
  },
  {
    head: 'Data Security',
    body: [
      'We implement reasonable safeguards to protect your personal information from unauthorized access, loss, or misuse. However, no security system is entirely foolproof, and we cannot guarantee absolute security.',
    ],
  },
  {
    head: "Children's Privacy",
    body: [
      'Our Website is not intended for children under 13, and we do not knowingly collect information from them. If we become aware that we have collected personal information from a child under 13, we will delete it promptly.',
    ],
  },
  {
    head: 'Your Rights',
    body: ['You have the right to:'],
    list: [
      'Request access to the personal information we hold about you.',
      'Request corrections to your information.',
      'Opt out of receiving emails or communications from us by following the "unsubscribe" link in emails or contacting us directly.',
    ],
  },
  {
    head: 'Changes to this Policy',
    body: [
      'We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Updates will be posted on this page with the revised date.',
    ],
  },
  {
    head: 'Contact Us',
    body: ['If you have any questions or concerns about this Privacy Policy, please contact us at:'],
    contact: [
      { label: 'Email',   value: 'office@kbtla.org' },
      { label: 'Address', value: 'Kehilat Bnei Torah · 9017 W. Pico Blvd., Los Angeles, CA 90035' },
    ],
  },
];

function PolicyBlock({ section, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: index * 0.03, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        sx={{
          pt: 5,
          pb: 5,
          borderTop: '1px solid rgba(36,34,88,0.10)',
        }}
      >
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: { xs: 22, md: 28 },
            color: 'var(--navy-500)',
            letterSpacing: '-0.01em',
            mb: 2,
          }}
        >
          {section.head}
        </Box>

        <Stack spacing={2}>
          {section.body?.map((p, i) => (
            <Box
              key={i}
              sx={{
                fontFamily: 'var(--font-body)',
                fontSize: { xs: 15.5, md: 16.5 },
                lineHeight: 1.7,
                color: 'var(--text)',
                opacity: 0.92,
              }}
            >
              {p}
            </Box>
          ))}

          {section.list && (
            <Stack
              spacing={1.5}
              component="ul"
              sx={{
                m: 0,
                pl: 0,
                listStyle: 'none',
              }}
            >
              {section.list.map((item, i) => (
                <Stack
                  key={i}
                  component="li"
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      mt: 1,
                      flex: '0 0 auto',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: 'var(--teal-500)',
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontSize: { xs: 15.5, md: 16.5 },
                      lineHeight: 1.65,
                      color: 'var(--text)',
                      opacity: 0.92,
                    }}
                  >
                    {item}
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}

          {section.contact && (
            <Box
              sx={{
                mt: 2,
                p: 3,
                background: 'var(--cream-50)',
                border: '1px solid rgba(36,34,88,0.10)',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' },
                rowGap: 1.5,
                columnGap: 3,
              }}
            >
              {section.contact.map((row) => (
                <React.Fragment key={row.label}>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {row.label}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'var(--navy-500)',
                    }}
                  >
                    {row.value}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Stack>
      </Box>
    </motion.div>
  );
}

export default function Privacy() {
  return (
    <EditorialPageShell
      title="Privacy Policy"
      description="Privacy Policy for Kehilat Bnei Torah — how we collect, use, and safeguard your information."
    >
      <PageHero
        kicker="Legal  ·  KBT"
        meta="Version 1/1/2023"
        title="Privacy"
        titleAccent="policy."
        subtitle="How Kehilat Bnei Torah collects, uses, and safeguards your information when you visit kbtla.org and our member services."
        image="/assets/images/kbt/hero-sy.jpg"
        height="60vh"
      />

      <EditorialSection
        kicker="The Policy"
        title="What we collect,"
        titleAccent="and what we don't."
        align="left"
      >
        <Box sx={{ maxWidth: 820, mx: 'auto' }}>
          {SECTIONS.map((s, i) => (
            <PolicyBlock key={s.head} section={s} index={i} />
          ))}

          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: '1px solid rgba(36,34,88,0.10)',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Version date  ·  1 / 1 / 2023
          </Box>
        </Box>
      </EditorialSection>
    </EditorialPageShell>
  );
}
